/* eslint-disable */
import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { usePapaParse } from 'react-papaparse';
import { useFilePicker } from 'use-file-picker';
import { FileContent } from 'use-file-picker/dist/interfaces';
import { Dialog, DialogContent, MenuItem, TableBody, TableRow } from '@material-ui/core';
import axios from '../../services/axios';

import { setToast } from '../../store/actions/toast.actions';
// import schemas from '../../utils/schemas';

import GridContainer from '../../components/Grid/GridContainer';
import GridItem from '../../components/Grid/GridItem';
import SelectField from '../../components/SpecialInput/SelectField';
import tripsService from '../../services/trips.service';
import {
  Alert,
  Button,
  DialogActions,
  DialogTitle,
  IconButton,
  LinearProgress,
  Select,
  SelectChangeEvent,
  Table,
  TableCell,
  TableHead,
} from '@mui/material';
import { Delete, ImportContacts } from '@material-ui/icons';

import CSVMappingForm from './CSVMappingForm';

interface CSVMappingHeader {
  name: string;
  template?: string;
  required?: boolean;
  description?: string;
}

interface ImportError {
  errorMessage: string;
  errorAddrr: string;
}

interface CSVDialogProps {
  open: boolean;
  subject: string;
  title: string;
  // eslint-disable-next-line no-unused-vars
  setOpen(value: boolean): void;
}

function CSVDialog({ subject, title, open, setOpen }: CSVDialogProps) {
  const [loaded, setLoaded] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [headers, setHeaders] = useState<string[]>([]);
  const [importOptions, setImportOptions] = useState<any>(null);
  const [allRequiredFields, setAllRequiredFields] = useState(false);
  const [templateValues, setTemplateValues] = useState<CSVMappingHeader[]>([]);
  const [step, setStep] = useState('fileSelect');
  const [error, setError] = useState<string>();
  const [openFileSelector, { loading, filesContent, plainFiles, clear }] = useFilePicker({
    accept: '.csv',
    multiple: false,
  });
  const { readString } = usePapaParse();
  const [mappingValues, setMappingValues] = useState<any>({});

  // Import error tracking
  const [importErrors, setImportErrors] = useState<ImportError[]>([]);
  const importErrorsRef = useRef<ImportError[]>([]);
  const lastParsedIndexRef = useRef(0);

  const ASSIGN_ONLY_FIELDS = ['unmatchedBehavior', 'shipToAddressee'];
  const CREATE_ONLY_FIELDS = [
    'warehouseId',
    'deliveryOrderExternalId',
    'packageCount',
    'packageSize',
    'customerExternalId',
    'customerFirstName',
    'customerLastName',
    'customerPhone',
  ];

  const computeFieldVisibility = (values: any): Record<string, boolean> => {
    if (subject !== 'trips') return {};
    const isAssignMode = values.importMode === 'default:assign';
    const isCreateFallback = isAssignMode && values.unmatchedBehavior !== 'default:ignore';
    const visibility: Record<string, boolean> = {};
    ASSIGN_ONLY_FIELDS.forEach((field) => {
      visibility[field] = isAssignMode;
    });
    CREATE_ONLY_FIELDS.forEach((field) => {
      visibility[field] = !isAssignMode || isCreateFallback;
    });
    return visibility;
  };

  const computeFieldRequired = (values: any): Record<string, boolean> => {
    if (subject !== 'trips') return {};
    const isAssignMode = values.importMode === 'default:assign';
    return {
      importMode: true,
      unmatchedBehavior: true,
      tripStatus: true,
      courierExternalSource: true,
      courierDefaultId: false,
      warehouseId: !isAssignMode,
      shipToAddressee: isAssignMode,
    };
  };

  const fieldVisibility = computeFieldVisibility(mappingValues);
  const fieldRequired = computeFieldRequired(mappingValues);

  const getSettingsHeaders = () => {
    if (!importOptions?.mappingHeaders) return [];
    return importOptions.mappingHeaders.filter((mapping: any) =>
      mapping.fields?.every((f: any) => f.type === 'Select'),
    );
  };

  const getColumnHeaders = () => {
    if (!importOptions?.mappingHeaders) return [];
    return importOptions.mappingHeaders.filter((mapping: any) =>
      !mapping.fields?.every((f: any) => f.type === 'Select'),
    );
  };

  const parseStreamErrors = (responseText: string) => {
    const newText = responseText.substring(lastParsedIndexRef.current);
    if (!newText) return;

    // Match top-level tokens: double-quoted strings OR flat JSON objects
    const tokenRegex = /"(?:[^"\\]|\\.)*"|\{[^{}]*\}/g;
    let match;
    let lastMatchEnd = 0;
    while ((match = tokenRegex.exec(newText)) !== null) {
      lastMatchEnd = match.index + match[0].length;
      const token = match[0];
      try {
        let parsed: any;
        if (token.startsWith('"')) {
          // Double-encoded JSON string — decode twice
          parsed = JSON.parse(JSON.parse(token));
        } else {
          parsed = JSON.parse(token);
        }
        if (parsed.importErrors) {
          importErrorsRef.current = parsed.importErrors;
          setImportErrors([...parsed.importErrors]);
        }
      } catch (e) {
        // Skip unparseable tokens
      }
    }
    // Only advance past fully matched content
    lastParsedIndexRef.current += lastMatchEnd;
  };

  const submitForm = () => {
    setStep('submitting');
    setImportErrors([]);
    importErrorsRef.current = [];
    lastParsedIndexRef.current = 0;

    const formData: FormData = new FormData();

    formData.append('csv', plainFiles[0]);
    Object.keys(mappingValues).forEach((key) => {
      if (fieldVisibility[key] === false) return;
      formData.append(key, mappingValues[key]);
    });

    localStorage.setItem(`CSV-map-template-${subject}`, JSON.stringify(mappingValues));
    setSubmitting(true);

    axios
      .post(`/csv_import/${subject}`, formData, {
        onDownloadProgress: (event: any) => {
          const responseText: string = event.currentTarget?.response ?? '';
          parseStreamErrors(responseText);
        },
      })
      .then((result) => {
        const responseText = typeof result.data === 'string' ? result.data : JSON.stringify(result.data);
        parseStreamErrors(responseText);
        setStep('summary');
      })
      .catch((err) => {
        setError(err.message);
        setStep('error');
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  useEffect(() => {
    if (loading) {
      setLoaded(false);
    }
  }, [loading, setLoaded]);

  useEffect(() => {
    if (filesContent && filesContent.length !== 0 && !loaded) {
      setStep('options');
      readString(filesContent[0].content, {
        header: true,
        worker: true,
        complete: (results) => {
          setHeaders(results.meta.fields!);
          const mapSavedTemplateWithHeaders = (fieldName: string) => {
            return results.meta.fields!.includes(fieldName) ? fieldName : '';
          };
          // load options
          axios
            .get(`/csv_import/options/${subject}`)
            .then((csvOptions) => {
              // console.log(csvOptions.data.data);
              setImportOptions(csvOptions.data.data);
            })
            .catch((err) => {
              setError(err.message);
              setStep('error');
            });
          const template = localStorage.getItem(`CSV-map-template-${subject}`);
          if (template) {
            const templateVals = JSON.parse(template);
            setMappingValues(templateVals);
            setAllRequiredFields(templateVals);
          }
        },
      });

      setLoaded(true);
    }
  }, [filesContent, loaded, setLoaded, readString]);

  const handleClose = () => {
    setOpen(false);
    clear();
    setStep('fileSelect');
    setImportErrors([]);
    importErrorsRef.current = [];
    lastParsedIndexRef.current = 0;
  };

  const checkAllRequiredFields = (values: any) => {
    const visibility = computeFieldVisibility(values);
    const required = computeFieldRequired(values);
    setAllRequiredFields(
      importOptions.mappingHeaders?.findIndex((mapping: any) =>
        mapping.fields?.some((field: any) => {
          const isRequired = required[field.field] !== undefined ? required[field.field] : field.required;
          return isRequired && visibility[field.field] !== false && !values[field.field];
        }),
      ) < 0,
    );
  };
  const fieldChangeHandler = (field: string, value: string) => {
    const values = { ...mappingValues };
    values[field] = value ? value : undefined;
    // Clear hidden fields when switching modes
    const visibility = computeFieldVisibility(values);
    Object.keys(visibility).forEach((key) => {
      if (!visibility[key]) {
        delete values[key];
      }
    });
    setMappingValues(values);
    checkAllRequiredFields(values);
  };
  const fileSelected = filesContent.length > 0;
  return (
    <Dialog open={open} onClose={handleClose} disableEnforceFocus>
      <DialogTitle>{title}</DialogTitle>
      {step === 'fileSelect' && (
        <>
          <DialogContent>
            <div className="import">
              {fileSelected ? (
                <>
                  <h4>Import from:</h4>
                  <h3>
                    <IconButton
                      onClick={() => {
                        clear();
                      }}
                    >
                      <Delete color="error" />
                    </IconButton>
                    {filesContent && filesContent[0] ? filesContent[0].name : ''}
                  </h3>
                </>
              ) : (
                <>
                  <Button onClick={openFileSelector} variant="outlined">
                    Select a files
                  </Button>
                </>
              )}
            </div>
          </DialogContent>
          <DialogActions>
            <Button
              disabled={!fileSelected}
              color="success"
              variant="outlined"
              onClick={() => {
                setStep('options');
              }}
            >
              Next
            </Button>
          </DialogActions>
        </>
      )}
      {step === 'options' && (
        <>
          <DialogContent>
            <div>{filesContent && filesContent[0] ? filesContent[0].name : ''}</div>
            {importOptions ? (
              <>
                {getSettingsHeaders().length > 0 && (
                  <>
                    <Alert severity="info" sx={{ mb: 1 }}>Import Settings</Alert>
                    <CSVMappingForm
                      columns={headers}
                      mappingHeaders={getSettingsHeaders()}
                      mappingValues={mappingValues}
                      fieldVisibility={fieldVisibility}
                      fieldRequired={fieldRequired}
                      setValue={fieldChangeHandler}
                    />
                  </>
                )}
                <Alert severity="info" sx={{ mt: 2, mb: 1 }}>
                  We need to locate the following information in your CSV. What´s the name of the column that has each
                  of these data?
                </Alert>
                <CSVMappingForm
                  columns={headers}
                  mappingHeaders={getColumnHeaders()}
                  mappingValues={mappingValues}
                  fieldVisibility={fieldVisibility}
                  fieldRequired={fieldRequired}
                  setValue={fieldChangeHandler}
                />
              </>
            ) : (
              <LinearProgress />
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setStep('fileSelect');
              }}
            >
              Back
            </Button>
            <Button disabled={!allRequiredFields} color="success" variant="outlined" onClick={submitForm}>
              Import
            </Button>
          </DialogActions>
        </>
      )}
      {step === 'submitting' && (
        <>
          <DialogContent>
            <div>
              Importing <strong>{filesContent && filesContent[0] ? filesContent[0].name : ''}</strong> ...
            </div>
            <LinearProgress sx={{ mt: 1 }} />
          </DialogContent>
          <DialogActions>
            <Button
              disabled={true}
              variant="outlined"
              onClick={() => {
                handleClose();
              }}
            >
              Close
            </Button>
          </DialogActions>
        </>
      )}
      {step === 'summary' && (
        <>
          <DialogContent>
            <Alert severity={importErrors.length > 0 ? 'warning' : 'success'} sx={{ mb: 2 }}>
              Import completed
            </Alert>
            {importErrors.length > 0 && (
              <>
                <Alert severity="error" sx={{ mb: 1 }}>
                  {importErrors.length} error{importErrors.length > 1 ? 's' : ''} during import
                </Alert>
                <div
                  style={{
                    maxHeight: 300,
                    overflowY: 'auto',
                    backgroundColor: '#1e1e1e',
                    color: '#d4d4d4',
                    fontFamily: 'monospace',
                    fontSize: 12,
                    padding: 8,
                    borderRadius: 4,
                  }}
                >
                  {importErrors.map((err, i) => (
                    <div key={i} style={{ marginBottom: 4 }}>
                      <span style={{ color: '#f44336' }}>[{err.errorAddrr}]</span>{' '}
                      {err.errorMessage}
                    </div>
                  ))}
                </div>
              </>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              variant="outlined"
              onClick={() => {
                handleClose();
              }}
            >
              Close
            </Button>
          </DialogActions>
        </>
      )}
      {step === 'error' && (
        <>
          <DialogContent>
            <div>
              Importing <strong>{filesContent && filesContent[0] ? filesContent[0].name : ''}</strong> failed.
            </div>
            <Alert severity="error">{error}</Alert>
          </DialogContent>
          <DialogActions>
            <Button
              color="error"
              variant="outlined"
              onClick={() => {
                handleClose();
              }}
            >
              Close
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}

export default CSVDialog;
